interface NotificationData {
  type: string;
  conversationId?: string;
  senderName?: string;
  severity?: string;
  workOrderId?: string;
}

class NotificationService {
  private permission: NotificationPermission = 'default';
  private mockMode = true; // Set to false when real backend is available

  constructor() {
    this.checkPermission();
  }

  private checkPermission() {
    if ('Notification' in window) {
      this.permission = Notification.permission;
    }
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return 'denied';
    }

    if (this.permission === 'granted') {
      return 'granted';
    }

    if (this.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission;
    }

    return this.permission;
  }

  async showNotification(
    title: string,
    options: {
      body?: string;
      icon?: string;
      badge?: string;
      tag?: string;
      data?: NotificationData;
      requireInteraction?: boolean;
      silent?: boolean;
    } = {},
  ): Promise<void> {
    if (!('Notification' in window)) {
      console.warn('Notifications not supported');
      return;
    }

    if (this.permission !== 'granted') {
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        return;
      }
    }

    // Check if the page is visible, don't show notification if user is actively using the app
    if (document.visibilityState === 'visible' && !options.requireInteraction) {
      return;
    }

    const notificationOptions: NotificationOptions = {
      body: options.body,
      icon: options.icon || '/favicon.ico',
      badge: options.badge || '/favicon.ico',
      tag: options.tag,
      data: options.data,
      requireInteraction: options.requireInteraction || false,
      silent: options.silent || false,
      ...options,
    };

    try {
      const notification = new Notification(title, notificationOptions);

      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();

        // Handle notification click based on data
        if (options.data?.type) {
          this.handleNotificationClick(options.data);
        }

        notification.close();
      };

      // Auto-close notification after 5 seconds unless requireInteraction is true
      if (!options.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 5000);
      }
    } catch (error) {
      console.error('Failed to show notification:', error);
    }
  }

  private handleNotificationClick(data: NotificationData) {
    switch (data.type) {
      case 'message':
        // Navigate to messages
        window.location.hash = '#/communication/messages';
        break;
      case 'emergency':
        // Navigate to emergency alerts
        window.location.hash = '#/emergency-alerts';
        break;
      case 'announcement':
        // Navigate to announcements
        window.location.hash = '#/communication/announcements';
        break;
      case 'voting':
        // Navigate to governance
        window.location.hash = '#/governance';
        break;
      case 'maintenance':
        // Navigate to work orders
        window.location.hash = '#/work-orders';
        break;
      default:
        // Navigate to dashboard
        window.location.hash = '#/';
    }
  }

  showMessageNotification(senderName: string, message: string, conversationId: string) {
    this.showNotification(`New message from ${senderName}`, {
      body: message.length > 100 ? message.substring(0, 100) + '...' : message,
      tag: `message_${conversationId}`,
      data: {
        type: 'message',
        conversationId,
        senderName,
      },
    });
  }

  showEmergencyAlert(
    title: string,
    message: string,
    severity: 'info' | 'warning' | 'critical' | 'emergency',
  ) {
    const requireInteraction = severity === 'emergency' || severity === 'critical';

    this.showNotification(`🚨 ${title}`, {
      body: message,
      requireInteraction,
      tag: 'emergency_alert',
      data: {
        type: 'emergency',
        severity,
      },
    });
  }

  showAnnouncementNotification(title: string, preview: string) {
    this.showNotification(`📢 ${title}`, {
      body: preview,
      tag: 'announcement',
      data: {
        type: 'announcement',
      },
    });
  }

  showVotingNotification(title: string, description: string) {
    this.showNotification(`🗳️ ${title}`, {
      body: description,
      requireInteraction: true,
      tag: 'voting_alert',
      data: {
        type: 'voting',
      },
    });
  }

  showMaintenanceNotification(title: string, description: string, workOrderId: string) {
    this.showNotification(`🔧 ${title}`, {
      body: description,
      tag: `maintenance_${workOrderId}`,
      data: {
        type: 'maintenance',
        workOrderId,
      },
    });
  }

  // Check if notifications are supported and permission is granted
  isSupported(): boolean {
    return 'Notification' in window;
  }

  hasPermission(): boolean {
    return this.permission === 'granted';
  }

  getPermissionStatus(): NotificationPermission {
    return this.permission;
  }

  // Clear all notifications with a specific tag
  clearNotifications(tag: string) {
    // Modern browsers don't provide a way to programmatically close notifications
    // This would typically be handled by the service worker
    console.log(`Clearing notifications with tag: ${tag}`);
  }

  // Service Worker registration for advanced notification features
  async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service workers not supported');
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service worker registered successfully');
      return registration;
    } catch (error) {
      console.error('Service worker registration failed:', error);
      return null;
    }
  }

  // Set mock mode for development
  setMockMode(enabled: boolean) {
    this.mockMode = enabled;
  }
}

// Singleton instance
export const notificationService = new NotificationService();
export default notificationService;
